/**
 * Geocode Tools for FlowRide Frontend
 * Provides utility functions for geocoding-related operations, including icon paths based on transport modes.
 */

import MaPositionIcon from "../image/icons/Inputs/MyLocationIcon.png";
import { GeocodeItem, GeocodeSource, GeocodeTransportMode, GeocodeType, SearchResultItem } from "../types/geocode-ui.types";


/**
 * Get the icon path for a transport mode
 * 
 * @param {GeocodeTransportMode} mode - The transport mode to get icon for
 * @returns {string} Path to the icon SVG file
 */
export const getTransportModeIconSource = ( mode: GeocodeTransportMode ): string => {
    switch ( mode ) {
        case GeocodeTransportMode.BUS:
            return "/assets/icon/geocoding/transportation/bus.svg";

        case GeocodeTransportMode.COACH: // Fallthrough

        case GeocodeTransportMode.METRO: // Fallthrough

        case GeocodeTransportMode.SUBWAY: // Fallthrough

        case GeocodeTransportMode.SUBURBAN: // Fallthrough

        case GeocodeTransportMode.TRAM:
            return "/assets/icon/geocoding/transportation/tram.svg";

        case GeocodeTransportMode.RAIL: // Fallthrough

        case GeocodeTransportMode.HIGHSPEED_RAIL: // Fallthrough

        case GeocodeTransportMode.LONG_DISTANCE: // Fallthrough

        case GeocodeTransportMode.NIGHT_RAIL: // Fallthrough

        case GeocodeTransportMode.REGIONAL_RAIL: // Fallthrough

        case GeocodeTransportMode.REGIONAL_FAST_RAIL: // Fallthrough
            return "/assets/icon/geocoding/transportation/train.svg";

        case GeocodeTransportMode.BIKE:
            return "/assets/icon/geocoding/transportation/bike.svg";

        case GeocodeTransportMode.AIRPLANE:
            return "/assets/icon/geocoding/transportation/airplane.svg";

        case GeocodeTransportMode.FERRY:
            return "/assets/icon/geocoding/transportation/ship.svg";

        default:
            return "/assets/icon/geocoding/transportation/bus.svg";
    }
};

export const getGeocodeItemSubtitle = ( item: GeocodeItem ): string => {
    if ( !item.area ) {
        return "";
    }

    let areaindex = item.area.default;
    let name = item.area.tree[areaindex].name;

    while ( item.result === name && item.area.tree.length > areaindex + 1 ) {
        areaindex++;
        name = item.area.tree[areaindex].name;
    }

    if ( item.source === GeocodeSource.MYLOCATION ) {
        return `${ item.result }, ${ name }`;
    }

    return name;
};

/**
 * Renders the appropriate icon for a geocode item
 * 
 * @param {SearchResultItem} item - The geocode item to render icon for
 * @returns {{ "alt": string, "src": string }} icon description
 */
export const getGeocodeIcon = ( item: GeocodeItem ): {
    "alt": string;
    "src": string;
} => {
    const result = {
        "alt": "",
        "src": "",
    };

    if ( item.category && ( item.source === GeocodeSource.MOTIS || item.source === GeocodeSource.LOCAL ) ) {
        result.alt = item.category.name;
        result.src = `/assets/icon/geocoding/osm/${ item.category.icon }.svg`;
    } else if ( item.source === GeocodeSource.MYLOCATION ) {
        result.alt = "Ma position";
        result.src = MaPositionIcon;
    } else if ( item.source === GeocodeSource.GOOGLE ) {
        result.alt = "Google";
        result.src = "/assets/icon/geocoding/google.svg";
    } else if ( item.type === GeocodeType.PLACE ) {
        result.alt = "place";
        result.src = "/assets/icon/geocoding/place.svg";
    } else if ( item.type === GeocodeType.ADDRESS ) {
        result.alt = "address";
        result.src = "/assets/icon/geocoding/address.svg";
    } else if ( item.type === GeocodeType.STOP ) {
        if ( item.modes && item.modes.length > 0 ) {
            result.src = getTransportModeIconSource( item.modes[0] );
        } else {
            result.src = "/assets/icon/geocoding/transportation/bus.svg";
        }
    }

    return result;
};

export const convertGeocodeItemToSearchResultItem = ( item: GeocodeItem ): SearchResultItem => {
    if ( item.source === GeocodeSource.MYLOCATION ) {
        return {
            ...item,
            "title":    "Ma position",
            "subtitle": "",
            "icon":     {
                "alt": "Ma position",
                "src": MaPositionIcon,
            },
            "resume": "Ma position",
        };
    }

    const subtitle = getGeocodeItemSubtitle( item );

    return {
        ...item,
        "title":    item.result,
        "subtitle": subtitle,
        "icon":     getGeocodeIcon( item ),
        "resume":   subtitle !== "" ? `${ item.result }, ${ subtitle }` : item.result,
    };
};
